import { useState, useRef, useCallback } from "react";
import { message } from "antd";
import { usePutPostAPI } from "utilFunctions/CustomHooks/usePutPostAPI";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import {
  deleteAPI,
  getAPIRequest,
} from "utilFunctions/Axios/AxiosRequestConfiguration";
import { BASE_URL } from "utilFunctions/Constants/urlConstants";
import getRequestInterface from "utilFunctions/Axios/getRequestInterface";

export const useChannelsPost = (
  form?: any,
  options: { onSuccess?: (data: any) => void; selectedPostForUpdate?: any } = {}
) => {
  const { onSuccess, selectedPostForUpdate } = options;
  const [posts, setPosts] = useState<any[]>([]);
  const [isPostLoading, setIsPostLoading] = useState(false);
  const abortControllersRef = useRef(new Map());
  const currentFilesRef = useRef<any[]>([]);
  const currentFormDataRef = useRef<any>(null);
  const offset = useRef(0);
  const [hasMore, setHasMore] = useState(false);
  const loadedPostIds = useRef(new Set());
  const LIMIT = 10;

  const onFetchSuccess = useCallback(
    (data: any) => {
      console.log("Fetched posts data:", data);
      if (data?.data && data.data.length > 0) {
        const newData =
          offset.current === 0 ? data.data : [...posts, ...data.data];

        console.log("Fetched posts data:", newData);
        setPosts(newData);
        setHasMore(true);
      } else {
        console.log("Fetched posts data:");
        setHasMore(false);
        if (offset.current === 0) {
          setPosts([]);
          loadedPostIds.current.clear();
        }
      }
      setIsPostLoading(false);
    },
    [posts, setPosts, setHasMore, setIsPostLoading, LIMIT]
  );

  const onFetchError = useCallback(
    (error: any) => {
      message.error(error.message);
      setIsPostLoading(false);
    },
    [setIsPostLoading]
  );

  const { makeRequest: fetchPosts, loading: postsLoading } = useGetApi(
    onFetchSuccess,
    onFetchError
  );

  const loadPosts = useCallback(
    (url: string, isRefresh = false) => {
      if (isRefresh) {
        offset.current = 0;
      }
      setIsPostLoading(true);
      fetchPosts(`${url}&offset=${offset.current}&limit=${LIMIT}`);
    },
    [fetchPosts]
  );

  const postsLoadingRef = useRef(postsLoading);
  postsLoadingRef.current = postsLoading;

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  const next = useCallback(
    (url: string) => {
      if (postsLoadingRef.current || !hasMoreRef.current) return;
      offset.current += LIMIT;
      loadPosts(url);
    },
    [loadPosts]
  );

  const { makeRequest: createPost, loading: createPostLoading } = usePutPostAPI(
    (data) => {
      message.success("Created successfully!");
      if (onSuccess) onSuccess(data.data);
    },
    (error) => {
      message.error(error.message || "Failed to create post.");
    },
    "POST"
  );

  const { makeRequest: updatePost, loading: updatePostLoading } = usePutPostAPI(
    (data) => {
      console.log("Post updated:", data);
      message.success("Updated successfully!");
      if (onSuccess) onSuccess(selectedPostForUpdate);
    },
    (error) => {
      message.error(error.message || "Failed to update post.");
    },
    "PUT"
  );
  const { makeRequest: updatePostPublish, loading: updatePostPublishLoading } =
    usePutPostAPI(
      (data) => {
        console.log("Post updated:", data);
        message.success("Published successfully!");
      },
      (error) => {
        message.error(error.message || "Failed to update post.");
      },
      "PUT"
    );

  const uploadFileToS3 = async (
    file: any,
    presignedUrl: string,
    signal: AbortSignal
  ) => {
    try {
      const response = await fetch(presignedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
        signal,
      });
      if (!response.ok) {
        throw new Error(`S3 upload failed: ${response.statusText}`);
      }
      return true;
    } catch (error: any) {
      if (error.name !== "AbortError") {
        console.error("Error uploading to S3:", error);
      }
      return false;
    }
  };
  const updateFileStatus = (file: any, status: any, response?: any) => {
    if (form) {
      const { Image, Ebook, videos } = form.getFieldsValue([
        "Image",
        "Ebook",
        "videos",
      ]);
      const update = (fileList: any) =>
        fileList?.map((f: any) =>
          f.uid === file.uid ? { ...f, status, response } : f
        );
      form.setFieldsValue({
        Image: update(Image),
        Ebook: update(Ebook),
        videos: update(videos),
      });
    }
  };
  const processFileUploads = async (
    filesToUpload: any[],
    presignedUrls: any[]
  ) => {
    console.log("Processing file uploads", filesToUpload, presignedUrls);

    const uploadPromises = presignedUrls.map(async (presignedData: any) => {
      try {
        const originalFile = filesToUpload.find(
          (file: any) => file.name === presignedData.fileName
        );

        if (!originalFile) {
          message.error(
            `Could not find original file for ${presignedData.fileName}`
          );
          return null;
        }

        const controller = new AbortController();
        abortControllersRef.current.set(originalFile.uid, controller);
        console.log(
          "Uploading",
          originalFile.name,
          "to",
          presignedData.preSignedUrl
        );

        const uploadSuccess = await uploadFileToS3(
          originalFile.originFileObj,
          presignedData.preSignedUrl,
          controller.signal
        );
        abortControllersRef.current.delete(originalFile.uid);

        if (uploadSuccess) {
          updateFileStatus(originalFile, "done");
          console.log(`Successfully uploaded: ${originalFile.name}`);
          return {
            assetType: presignedData.assetType,
            url: presignedData.preSignedUrl.split(".com/")[1].split("?")[0],
            id: presignedData.contentRecordId,
            name: originalFile.name,
          };
        } else {
          if (controller.signal.aborted) {
            updateFileStatus(originalFile, "error", "Upload canceled");
            message.warning(`Upload of ${originalFile.name} was canceled.`);
          } else {
            updateFileStatus(originalFile, "error", "Upload failed");
            message.error(`Failed to upload ${originalFile.name} to S3.`);
          }
          return null;
        }
      } catch (error) {
        console.error(`Error uploading file ${presignedData.fileName}:`, error);
        message.error(`Failed to upload ${presignedData.fileName}`);
        return null;
      }
    });

    // Wait for all upload attempts to complete
    const results = await Promise.all(uploadPromises);

    // Filter out failed uploads (null values) and return only successful ones
    const successfulUploads = results.filter((result: any) => result !== null);

    console.log(
      `Upload completed: ${successfulUploads.length}/${results.length} files uploaded successfully`
    );

    if (successfulUploads.length < results.length) {
      const failedCount = results.length - successfulUploads.length;
      message.warning(
        `${failedCount} file(s) failed to upload. Proceeding with ${successfulUploads.length} successful uploads.`
      );
    }

    return successfulUploads;
  };

  const createPostWithAttachments = useCallback(
    (url: string, payload: any) => {
      console.log("Creating post with attachments. Payload:", payload);
      const filesToUpload = payload.attachments || [];
      currentFormDataRef.current = payload.formData || {};
      currentFilesRef.current = filesToUpload;

      if (filesToUpload.length > 0) {
        filesToUpload.forEach((file: any) =>
          updateFileStatus(file, "uploading")
        );
        const selectedFiles = filesToUpload.map((file: any) => ({
          fileName: file.name,
          assetType:
            file.type.split("/")[0] === "application" ? "Ebook" : "Image",
        }));
        getChannelPresignedUrls(url, selectedFiles);
      } else {
        handleFinalSubmission(payload.formData, []);
      }
    },
    [form]
  );

  const handleCancelUpload = useCallback((fileToCancel: any) => {
    console.log("Attempting to cancel upload for file:", fileToCancel);
    const controller = abortControllersRef.current.get(fileToCancel.uid);
    if (controller) {
      console.log("Cancelling upload for file UID:", fileToCancel.uid);
      controller.abort();
      abortControllersRef.current.delete(fileToCancel.uid);
      message.info(`Upload cancelled for ${fileToCancel.name}`);
    } else {
      console.warn("No controller found for file:", fileToCancel);
    }
  }, []);

  const handleFinalSubmission = (formData: any, attachments: any[]) => {
    const finalPayload = {
      ...formData,
      content: attachments,
    };

    if (selectedPostForUpdate && selectedPostForUpdate.id) {
      finalPayload.id = selectedPostForUpdate.id;
      updatePost(`${BASE_URL}post`, finalPayload);
    } else {
      createPost(`${BASE_URL}post`, finalPayload);
    }
  };

  const { makeRequest: getChannelPresignedUrls } = usePutPostAPI(
    async (data) => {
      try {
        if (data && data.data && currentFilesRef.current.length > 0) {
          const attachments = await processFileUploads(
            currentFilesRef.current,

            data.data
          );

          console.log(
            "File upload process completed. Attachments:",

            attachments
          );

          handleFinalSubmission(
            { ...currentFormDataRef.current },

            attachments
          );
        } else {
          handleFinalSubmission({ ...currentFormDataRef.current }, []);
        }
      } catch (error) {
        message.error("Failed to upload files.");

        currentFilesRef.current.forEach((file) =>
          updateFileStatus(file, "error", "Upload failed")
        );
      } finally {
        // Clear pending data

        currentFilesRef.current = [];

        currentFormDataRef.current = null;
      }
    },
    (error) => {
      message.error("Failed to get pre-signed URLs.");
      console.error("Error getting pre-signed URLs:", error);
      if (currentFilesRef.current.length > 0) {
        currentFilesRef.current.forEach((file) =>
          updateFileStatus(file, "error", "Pre-signed URL failed")
        );
      }
      if (currentFormDataRef.current) {
        handleFinalSubmission({ ...currentFormDataRef.current }, []);
      }
      // Clear pending data
      currentFilesRef.current = [];
      currentFormDataRef.current = null;
    },
    "POST"
  );

  const deletePost = (postId: string, onSuccess: () => void) => {
    deleteAPI({
      url: `${BASE_URL}post/${postId}`,
      success: () => {
        message.success("Deleted successfully!");
        onSuccess();
      },
      failure: (error: any) => {
        message.error(error.response?.data?.message || "Failed to delete");
      },
    });
  };

  const getSinglePost = async (
    postId: string,
    callback: (post: any) => void
  ) => {
    setIsPostLoading(true);
    await getAPIRequest({
      url: `${BASE_URL}post/${postId}`,
      success: (response: any) => {
        if (response.data.data) {
          const postData = response.data.data;
          const content = Array.isArray(postData.content)
            ? postData.content
            : [];
          console.log("Fetched single post data:", postData, content);
          const formattedPost = {
            ...postData,
            batchIds: postData.batchIds.map((b: any) => b.batchId),
            initialBatchOptions: postData.batchIds.map((b: any) => ({
              label: b.batchName,
              value: b.batchId,
            })),
            // Image: content.filter((c: any) => c.assetType === "Image"),
            // Ebook: content.filter((c: any) => c.assetType === "Ebook"),
            // videos: content.filter((c: any) => c.assetType === "videos"),
          };
          callback(formattedPost);
          setIsPostLoading(false);
        }
      },
      failure: (err: any) => {
        setIsPostLoading(false);
        message.error(err?.message);
      },
    } as getRequestInterface);
  };

  const deleteAttachment = (
    contentRecordId: string,
    postId: string,
    onSuccess: () => void
  ) => {
    deleteAPI({
      url: `${BASE_URL}post/content`,
      success: () => {
        message.success("Attachment deleted successfully!");
        onSuccess();
      },
      failure: (error: any) => {
        message.error(
          error.response?.data?.message || "Failed to delete attachment"
        );
      },
      body: {
        contentRecordId: contentRecordId,
        postId: postId,
      },
    });
  };

  return {
    posts,
    setPosts,
    isPostLoading,
    fetchPosts: loadPosts,
    postsLoading,
    createPost,
    createPostLoading,
    updatePost,
    updatePostLoading,
    updatePostPublish,
    updatePostPublishLoading,
    createPostWithAttachments,
    handleCancelUpload,
    deletePost,
    deleteAttachment,
    getSinglePost,
    hasMore,
    next,
  };
};
